import { cn, controlClasses, describedBy, Field, useFieldId } from '@gymcoach/ui';
import { useState, type ReactNode } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { authCopy } from './copy';

function Eye({ open }: { open: boolean }) {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {!open && <path d="M3 3l18 18" />}
    </svg>
  );
}

/** A password input bound to react-hook-form, with a show/hide button (DESIGN.md A1). */
export function PasswordField<T extends FieldValues>({
  form,
  name,
  label,
  hint,
  autoComplete,
  autoFocus,
  required = true,
}: {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: ReactNode;
  hint?: ReactNode;
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
  required?: boolean;
}) {
  const id = useFieldId();
  const [visible, setVisible] = useState(false);
  const error = (form.formState.errors[name] as { message?: unknown } | undefined)?.message;
  const message = typeof error === 'string' ? error : undefined;

  return (
    <Field id={id} label={label} hint={hint} error={message} required={required}>
      <div className="relative">
        <input
          {...form.register(name)}
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          spellCheck={false}
          autoCapitalize="none"
          aria-invalid={message ? true : undefined}
          aria-describedby={describedBy(id, hint, message)}
          className={cn(controlClasses(Boolean(message)), 'h-11 pr-11 text-lg sm:h-10 sm:text-base')}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? authCopy.newPassword.hide : authCopy.newPassword.show}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r text-ink-muted hover:text-ink"
        >
          <Eye open={!visible} />
        </button>
      </div>
    </Field>
  );
}
